import { Button, Checkbox, Col, Form, Input, message, Modal, Radio, Row, Select } from 'antd';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import ModalTitle from '../../common/components/ModalTitle';
import { ProjectsStore } from '../stores';
import { range } from 'lodash';

type Props = {
    setVisible: (visible: boolean) => void;
    store: ProjectsStore
};

const ProjectCreateDialog: React.FC<Props> = ({ setVisible, store}) => {
    const [form] = Form.useForm();
    const [isTouched, setIsTouched] = React.useState(false);

    React.useEffect(() => {
        if (!store.analyzers?.length) {
            store.getAnalyzers();
        } 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCreateSubmit = () => {
        form.validateFields().then((formValues) => {
            const obj = {
                ...formValues,
                analyzerId: formValues.analyzer,
                typeId: formValues.type,
                certificationId: formValues.certification
            };
            store.createNewProject(obj).then(() => {
                message.success('Project has been successfully created');
                handleCancel();
                store.getProjects();
            }).catch(err => {
                console.error(err);
                message.error('Failed to create project');
            });
        }).catch((err) => {
            console.log(err);
        });
    };

    const handleUpdateSubmit = () => {
        form.validateFields().then((formValues) => {
            const obj = {
                ...formValues,
                analyzerId: formValues.analyzer,
            };
            store.updateProject(store.selectedProject, obj).then(() => {
                message.success('Changes have been successfully saved');
                handleCancel();
                store.getProjects();
            }).catch(err => {
                console.error(err);
                message.error('Failed to update project');
            });
        }).catch((err) => {
            console.log(err);
        });
    };


    const handleCancel = () => {
        setVisible(false);
        form.resetFields();
        store.setSelectedProject('');
    };

    const editableProject = React.useMemo(
        () => store.projects.find(p => p.id === store.selectedProject),
        [store.projects, store.selectedProject]
    );

    const getInitialValues = () => {
        if (!editableProject) {
            return {};
        }
        return {
            analyzer: editableProject.analyzerId,
            ...editableProject
        };
    };
    
    const resultOptions = React.useMemo(
        () => range(editableProject ? editableProject.resultCount : 1, 11).map(i => ({ label: i, value: i })),
        [editableProject]
    );
    
    return (
        <Modal
            className="roche-client-dialog large-input"
            onCancel={handleCancel}
            visible={!store.isProjectDataLoading}
            closable={false}
            maskClosable={false}
            width={660}
            title={<ModalTitle title={editableProject ? 'Edit Project' : 'New Project'} onCloseCallback={handleCancel}/>}
            footer={[(
                <Button 
                    key="cancel-button"
                    className="light"
                    size="large"
                    onClick={handleCancel}
                >
                    Cancel
                </Button>
            ), (
                <Button 
                    data-testid="project-create-dialog-submit"
                    key="submit-button"
                    type="primary"
                    size="large"
                    onClick={editableProject ? handleUpdateSubmit : handleCreateSubmit}
                    disabled={editableProject ? !isTouched : false}
                >
                    {editableProject ? 'Save changes' :  'Create project'}
                </Button>
            )
            ]}
        >
            <Form
                form={form}
                initialValues={getInitialValues()}
                layout="vertical"
                data-testid="create-project-form"
                onFieldsChange={() => setIsTouched(true)}
            >
                <Form.Item
                    name="name"
                    label="Project Name"
                    rules={[{ required: true, message: 'Please input project name!', whitespace: true }]}
                >
                    <Input />
                </Form.Item>

                <Row gutter={20}>
                    <Col span={12}>
                        <Form.Item
                            name="analyzer"
                            label="Instruments"
                            rules={[{ required: true, message: 'Please select instrument!', whitespace: true }]}
                        >
                            <Select
                                showSearch
                                filterOption={(input, option) =>
                                    option!.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                                loading={store.isLoadingAnalyzers}
                                options={store.analyzers.map(i => {
                                    return { label: i.name, value: i.id };
                                })}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="resultCount"
                            label="Number of results"
                            rules={[{ required: true, message: 'Please select number of results!' }]}
                        >
                            <Select options={resultOptions} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={20}>
                    <Col span={12}>
                        <Form.Item name="productName" label="Product Name">
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="developmentLead" label="Development Lead">
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>

                <div>
                    <div className="ant-form-item-label">
                        <label>Application</label>
                    </div>

                    <Row gutter={20}>
                        <Col span={12}>
                            <Form.Item valuePropName="checked" name="applicationIsNormal" label={undefined}>
                                <Checkbox>Normal</Checkbox>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item valuePropName="checked" name="applicationIsStat" label={undefined}>
                                <Checkbox>STAT</Checkbox>
                            </Form.Item>
                        </Col>
                    </Row>
                </div>

                <Row gutter={20}>
                    <Col span={12}>
                        <Form.Item name="gpid" label="GPID">
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="lpn" label="LPN">
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={20}>
                    <Col span={12}>
                        <Form.Item name="diaDocVersion" label="DIAdoc Version">
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="kitSize" label="Kit size">
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={20}>
                    <Col span={12}>
                        <Form.Item name="materialNumberAssay" label="Material number assay">
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="msVersionAssay" label="MS Version assay">
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={20}>
                    <Col span={12}>
                        <Form.Item name="materialNumberCalSet" label="Material number CalSet">
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="msVersionCalSet" label="MS Version CalSet">
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item name="nameOfTheCalSet" label="Name of the CalSet">
                    <Input />
                </Form.Item>

                <Form.Item name="inpack" label="Inpack">
                    <Radio.Group>
                        <Radio value="yes">Yes</Radio>
                        <Radio value="no">No</Radio>
                    </Radio.Group>
                </Form.Item>

                <Row gutter={20}>
                    <Col span={12}>
                        <Form.Item name="materialNumbersPreciControl1" label="Material numbers PreciControl 1">
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="namesPreciControl1" label="Names PreciControl 1">
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={20}>
                    <Col span={12}>
                        <Form.Item name="msVersionPreciControl1" label="MS version PreciControl 1">
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="materialNumbersPreciControl2" label="Material numbers PreciControl 2">
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={20}>
                    <Col span={12}>
                        <Form.Item name="namesPreciControl2" label="Names PreciControl 2">
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="msVersionPreciControl2" label="MS version PreciControl 2">
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default observer(ProjectCreateDialog);